import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchMidpointMediaCrmLead,
  patchMidpointMediaCrmLead,
} from "../../../api/midpointMediaAdminApi";
import { resolveClientPortalAssetUrl } from "../../../api/clientUploadApi";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../../components/clientPortal/ClientPortalStates";
import useMidpointMediaAdminView from "../../../hooks/useMidpointMediaAdminView";
import { formatClientDateTime } from "../../../utils/clientPortalFormat";
import "../../../styles/midpointMediaAdmin.css";

const STATUSES = ["new", "contacted", "qualified", "nurturing", "converted", "closed"];

function displayValue(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function isPdf(resume) {
  if (!resume) return false;
  if (String(resume.mimeType || "").toLowerCase().includes("pdf")) return true;
  return /\.pdf$/i.test(String(resume.fileName || resume.fileUrl || ""));
}

function whatsappHref(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

function isApplicationLead(lead) {
  const type = String(lead?.leadType || "").toLowerCase();
  const source = String(lead?.source || lead?.publicSource || "").toLowerCase();
  const component = String(lead?.sourceComponent || lead?.form?.sourceComponent || "").toLowerCase();
  return (
    type === "job_application" ||
    type === "careers_application" ||
    source.includes("careers") ||
    component.includes("venezuela-careers")
  );
}

export default function MidpointMediaAdminCrmLeadDetailPage() {
  const { leadId } = useParams();
  const { clientViewId, paths, viewBase } = useMidpointMediaAdminView();
  const [state, setState] = useState({ loading: true, error: "", lead: null, saving: false });
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!clientViewId || !leadId) return undefined;
    let cancelled = false;
    fetchMidpointMediaCrmLead(clientViewId, leadId)
      .then((body) => {
        if (cancelled) return;
        setState({ loading: false, error: "", lead: body.lead, saving: false });
        setNotes(body.lead?.internalNotes || "");
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error: error.message, lead: null, saving: false });
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId, leadId]);

  const lead = state.lead;
  const form = lead?.form || {};
  const application = isApplicationLead(lead);
  const resume = lead?.resume || null;
  const resumeUrl = resume?.fileUrl ? resolveClientPortalAssetUrl(resume.fileUrl) : "";
  const notesDirty = notes !== (lead?.internalNotes || "");

  const fieldGroups = useMemo(() => {
    if (!lead) return [];
    return [
      {
        title: "Contact",
        fields: [
          ["Full name", lead.name],
          ["Email", form.email || lead.email],
          ["Phone / WhatsApp", form.phone || lead.phone],
          ["City", form.city || lead.city],
          ["Preferred channel", form.preferredChannel || lead.preferredChannel || lead.preferredContactMethod],
        ],
      },
      {
        title: application ? "Role & qualifications" : "Inquiry",
        fields: application
          ? [
              ["Role applied for", form.roleLabel || lead.roleType],
              ["Spoken English", form.englishLevel || lead.englishLevel],
              ["Relevant experience", form.relevantExperience || lead.relevantExperience],
              ["Résumé file", form.resumeFileName || lead.resumeFileName],
            ]
          : [
              ["Type", lead.leadType],
              ["Source", lead.source || lead.publicSource],
            ],
      },
      ...(application
        ? [
            {
              title: "Interview availability",
              fields: [
                ["Preferred days", form.interviewDays || lead.interviewDays],
                ["Preferred windows", form.interviewWindows || lead.interviewWindows],
                ["Availability notes", form.availabilityNotes || lead.availabilityNotes],
              ],
            },
          ]
        : []),
      {
        title: "Submission details",
        fields: [
          ["Received", formatClientDateTime(lead.createdAt)],
          ["Updated", formatClientDateTime(lead.updatedAt)],
          ["Reference", lead.referenceId],
          ["Capture page", form.capturePage || lead.capturePage],
          ["Source", form.source || lead.source],
          ["Source component", form.sourceComponent || lead.sourceComponent],
        ],
      },
    ];
  }, [lead, form, application]);

  const onStatus = async (event) => {
    const status = event.target.value;
    setState((current) => ({ ...current, saving: true, error: "" }));
    try {
      const body = await patchMidpointMediaCrmLead(clientViewId, leadId, { status });
      setState((current) => ({ ...current, saving: false, lead: body.lead }));
      setNotes(body.lead?.internalNotes || notes);
    } catch (error) {
      setState((current) => ({ ...current, saving: false, error: error.message }));
    }
  };

  const onSaveNotes = async () => {
    setState((current) => ({ ...current, saving: true, error: "" }));
    try {
      const body = await patchMidpointMediaCrmLead(clientViewId, leadId, { internalNotes: notes });
      setState((current) => ({ ...current, saving: false, lead: body.lead }));
      setNotes(body.lead?.internalNotes || "");
    } catch (error) {
      setState((current) => ({ ...current, saving: false, error: error.message }));
    }
  };

  if (state.loading) return <ClientPortalLoading message="Loading lead…" />;
  if (state.error && !state.lead) return <ClientPortalError message={state.error} />;
  if (!state.lead) return <p className="empty-state">Lead not found.</p>;

  const whatsapp = whatsappHref(lead.phone);

  return (
    <div className="mm-admin-page">
      <header>
        <p className="mm-admin-kicker">
          <Link to={`${paths.base}/crm/leads`}>Leads</Link>
          {application ? (
            <>
              {" · "}
              <Link to={`${paths.base}/people/applications`}>Applications</Link>
            </>
          ) : null}
        </p>
        <h1>{lead.name}</h1>
        <p className="mm-admin-lead">
          {lead.referenceId ? `Reference ${lead.referenceId}. ` : ""}
          {lead.roleType || lead.leadType || "Lead"}
          {lead.city ? ` · ${lead.city}` : ""}
          {` · ${formatClientDateTime(lead.createdAt)}`}
        </p>
        <div className="mm-admin-actions" style={{ marginTop: 12 }}>
          {lead.opportunityId && viewBase ? (
            <Link className="btn btn-primary" to={`${viewBase}/opportunities/${lead.opportunityId}`}>
              Work in pipeline
            </Link>
          ) : null}
          {lead.email ? (
            <a className="btn btn-secondary" href={`mailto:${lead.email}`}>
              Email
            </a>
          ) : null}
          {lead.phone ? (
            <a className="btn btn-secondary" href={`tel:${lead.phone}`}>
              Call
            </a>
          ) : null}
          {whatsapp ? (
            <a className="btn btn-secondary" href={whatsapp} target="_blank" rel="noreferrer">
              WhatsApp
            </a>
          ) : null}
        </div>
      </header>

      {state.error ? <ClientPortalError message={state.error} /> : null}

      <section className="mm-admin-detail-grid">
        <article className="mm-admin-card">
          <h2>{application ? "Submitted form" : "Lead details"}</h2>
          <div className="mm-admin-fact-groups">
            {fieldGroups.map((group) => (
              <div key={group.title} className="mm-admin-fact-group">
                <h4>{group.title}</h4>
                <dl className="mm-admin-facts">
                  {group.fields.map(([label, value]) => (
                    <div key={label} className="mm-admin-facts__row">
                      <dt>{label}</dt>
                      <dd>{displayValue(value)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
          {lead.message ? (
            <div className="mm-admin-fact-group">
              <h4>{application ? "Full application summary" : "Message"}</h4>
              <pre className="mm-admin-pre">{lead.message}</pre>
            </div>
          ) : null}
        </article>

        <div>
          <article className="mm-admin-card">
            <h2>Work this lead</h2>
            <label className="mm-admin-field">
              Status
              <select value={lead.status} onChange={onStatus} disabled={state.saving}>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
            <label className="mm-admin-field">
              Internal notes
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={8}
                maxLength={8000}
                disabled={state.saving}
                placeholder={
                  application
                    ? "Interview feedback, follow-ups, decision notes…"
                    : "Follow-up notes, next steps…"
                }
              />
            </label>
            <div className="mm-admin-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={state.saving || !notesDirty}
                onClick={onSaveNotes}
              >
                {state.saving ? "Saving…" : "Save notes"}
              </button>
            </div>
            {lead.opportunityId && viewBase ? (
              <p className="muted" style={{ marginTop: 12 }}>
                Pipeline status stays in sync when you change this lead.{" "}
                <Link to={`${viewBase}/opportunities/${lead.opportunityId}`}>Open the opportunity</Link>
                .
              </p>
            ) : (
              <p className="muted" style={{ marginTop: 12 }}>
                No pipeline record yet. Open Applications to create one, or save this lead again.
              </p>
            )}
          </article>

          {application ? (
            <article className="mm-admin-card" style={{ marginTop: 14 }}>
              <h2>Résumé</h2>
              {!resumeUrl ? (
                <p className="empty-state">No résumé file is attached.</p>
              ) : (
                <>
                  <p className="muted">{resume.fileName || "Attached résumé"}</p>
                  <div className="mm-admin-actions" style={{ marginBottom: 12 }}>
                    <a className="btn btn-secondary" href={resumeUrl} target="_blank" rel="noreferrer">
                      Open in new tab
                    </a>
                  </div>
                  {isPdf(resume) ? (
                    <iframe
                      title={`Résumé for ${lead.name}`}
                      src={resumeUrl}
                      className="mm-admin-modal__frame"
                    />
                  ) : null}
                </>
              )}
            </article>
          ) : null}
        </div>
      </section>
    </div>
  );
}
